import { SocketClient } from "cbcore-ts"
import { nil, NO, UIColor, UINativeScrollView, UITextView, UIView, UIViewBroadcastEvent, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { formatRHDateTime } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHButtons, RHColors, RHFont, RHRadius, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyQuestionsCardViewTokens, RHOfferMessageThreadViewTokens, RHQuestionAnswerPairViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHOfferMessage } from "../../SocketClientFunctions"

const OFFER_MESSAGE_INPUT_ID = "offer-message-thread-input"


// ─────────────────────────────────────────────────────────────────────────────
// Single message bubble
// ─────────────────────────────────────────────────────────────────────────────

class OfferMessageBubbleView extends UIView {
    
    readonly senderLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHCompanyQuestionsCardViewTokens.lastUpdateFontWeight,
                fontFamily: RHFont.familyBase,
                lineHeight: "1.2"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly textLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHQuestionAnswerPairViewTokens.messageTextFontWeight,
                fontFamily: RHFont.familyBase,
                lineHeight: RHFont.lineHeightBase,
                overflow: "visible",
                overflowWrap: "anywhere",
                textOverflow: "clip",
                whiteSpace: "normal",
                wordBreak: "break-word"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly timestampLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHCompanyQuestionsCardViewTokens.lastUpdateFontWeight,
                fontFamily: RHFont.familyBase,
                lineHeight: "1.2"
            }
        })
        .addedAsSubviewToView(this)
    
    // Whether this bubble belongs to the currently logged-in user (right-hand side)
    _isSelf = NO
    
    constructor() {
        super();
        
        (this as OfferMessageBubbleView).configureWithObject({
            style: {
                borderRadius: `${RHRadius.large}px`,
                boxSizing: "border-box",
                boxShadow: "none"
            }
        })
    }
    
    private _configureTextLabelForMultiline() {
        const multilineStyle = {
            overflow: "visible",
            overflowWrap: "anywhere",
            textOverflow: "clip",
            whiteSpace: "normal",
            wordBreak: "break-word"
        }
        this.textLabel.configureWithObject({
            isSingleLine: NO,
            style: multilineStyle
        })
        this.textLabel.textElementView.configureWithObject({
            style: {
                ...multilineStyle,
                lineHeight: RHFont.lineHeightBase
            }
        })
    }
    
    configure(message: RHOfferMessage, isSelf: boolean) {
        this._isSelf = isSelf
        
        this.senderLabel.text = message.senderCompanyName
            || (message.senderRole === "procurer" ? "Tundmatu hankija" : "Tundmatu pakkuja")
        this.textLabel.text = message.text
        
        const date = new Date(message.createdAt)
        this.timestampLabel.text = formatRHDateTime(date)
        
        let backgroundColor = RHBadges.alertBannerSuccessBubbleBackground
        let borderGradient = RHBadges.alertBannerSuccessBubbleBorderGradient
        let textColor = RHBadges.alertBannerSuccessBubbleTextColor
        let metadataTextColor = RHColors.textTimestamp
        
        if (isSelf) {
            backgroundColor = RHBadges.alertBannerQuestionLightTealBlueBubbleBackground
            borderGradient = RHBadges.alertBannerQuestionLightTealBlueBubbleBorderGradient
            textColor = RHBadges.alertBannerQuestionLightTealBlueBubbleTextColor
            metadataTextColor = RHBadges.alertBannerQuestionLightTealBlueBubbleTextColor.colorWithAlpha(0.65)
        }
        
        ;(this as OfferMessageBubbleView).configureWithObject({
            backgroundColor,
            style: {
                background: `linear-gradient(${backgroundColor.stringValue}, ${backgroundColor.stringValue}) padding-box, ${borderGradient} border-box`,
                border: `${`${RHBadges.alertBannerGradientBorderWidth}px`} solid transparent`,
                borderColor: "transparent"
            }
        })
        
        this.senderLabel.configureWithObject({
            textColor: metadataTextColor,
            style: { textAlign: "left" }
        })
        this.textLabel.configureWithObject({
            textColor: textColor,
            style: { textAlign: "left" }
        })
        this._configureTextLabelForMultiline()
        this.timestampLabel.configureWithObject({
            textColor: metadataTextColor,
            style: { textAlign: "right" }
        })
    }
    
    preferredContentWidth(maxWidth: number) {
        const inset = RHOfferMessageThreadViewTokens.bubbleContentInset
        const metadataWidth =
            this.senderLabel.intrinsicContentWidth(RHOfferMessageThreadViewTokens.bubbleMetadataRowHeight)
            + this.timestampLabel.intrinsicContentWidth(RHOfferMessageThreadViewTokens.bubbleMetadataRowHeight)
            + RHSpacing.small
            + inset * 2
        const textWidth = this.textLabel.intrinsicContentWidth(0) + inset * 2
        return [
            maxWidth,
            [
                260,
                metadataWidth,
                textWidth
            ].max()
        ].min()
    }
    
    override intrinsicContentHeight(constrainingWidth: number = 0): number {
        const inset = RHOfferMessageThreadViewTokens.bubbleContentInset
        const contentWidth = [
            0,
            constrainingWidth - inset * 2
        ].max()
        return inset * 2
            + RHOfferMessageThreadViewTokens.bubbleMetadataRowHeight
            + RHSpacing.tiny
            + this.textLabel.intrinsicContentHeight(contentWidth)
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const inset = RHOfferMessageThreadViewTokens.bubbleContentInset
        const metadataRowHeight = RHOfferMessageThreadViewTokens.bubbleMetadataRowHeight
        const bounds = this.contentBoundsWithInset(inset)
        const timestampWidth = [
            bounds.width,
            this.timestampLabel.intrinsicContentWidth(
                metadataRowHeight) + RHSpacing.small
        ].min()
        const senderWidth = [
            0,
            bounds.width - timestampWidth - RHSpacing.small
        ].max()
        
        const metadataRowFrame = bounds.rectangleWithHeight(metadataRowHeight)
        
        metadataRowFrame
            .distributeViewsAlongWidth(
                [this.senderLabel, this.timestampLabel],
                undefined,
                RHSpacing.small,
                [senderWidth, timestampWidth]
            )
        
        metadataRowFrame
            .rectangleForNextRow(RHSpacing.tiny, this.textLabel)
            .assignedAsFrameOfView(this.textLabel)
    }
    
}


// ─────────────────────────────────────────────────────────────────────────────
// Scroll container that holds all bubbles
// ─────────────────────────────────────────────────────────────────────────────

class OfferMessageListView extends UINativeScrollView {
    
    _bubbles: OfferMessageBubbleView[] = []
    
    constructor() {
        super();
        (this as OfferMessageListView).configureWithObject({
            style: {
                overflowX: "hidden",
                overflowY: "auto"
            }
        })
    }
    
    setMessages(messages: RHOfferMessage[], currentUserRole: "procurer" | "offerer") {
        for (const bubble of this._bubbles) {
            bubble.removeFromSuperview()
        }
        this._bubbles = []
        
        for (const message of messages) {
            const bubble = new OfferMessageBubbleView()
            bubble.configure(message, message.senderRole === currentUserRole)
            bubble.addedAsSubviewToView(this)
            this._bubbles.push(bubble)
        }
        
        this.setNeedsLayout()
    }
    
    appendMessage(message: RHOfferMessage, currentUserRole: "procurer" | "offerer") {
        const bubble = new OfferMessageBubbleView()
        bubble.configure(message, message.senderRole === currentUserRole)
        bubble.addedAsSubviewToView(this)
        this._bubbles.push(bubble)
        this.setNeedsLayoutUpToRootView()
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        
        if (this._bubbles.length === 0) {
            return
        }
        
        const maxBubbleWidth = bounds.width * 0.72
        
        let current = bounds.rectangleWithHeight(0)
        
        for (const bubble of this._bubbles) {
            const bubbleWidth = bubble.preferredContentWidth(maxBubbleWidth)
            const bubbleHeight = bubble.intrinsicContentHeight(bubbleWidth)
            const rowFrame = current.rectangleForNextRow(RHSpacing.small, bubbleHeight)
            
            if (bubble._isSelf) {
                bubble.frame = rowFrame
                    .rectangleWithInsets(0, RHSpacing.xxLarge, 0, 0)
                    .rectangleWithWidth(bubbleWidth, 1)
            }
            else {
                bubble.frame = rowFrame
                    .rectangleWithWidth(bubbleWidth, 0)
            }
            
            current = rowFrame
        }
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        // This view fills a fixed height assigned by the parent — use 0 so
        // the parent's fixed height assignment drives the layout.
        return 0
    }
    
}


// ─────────────────────────────────────────────────────────────────────────────
// Main thread view
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A private 1:1 messaging thread between the procurer and the offerer company
 * for one offer package.
 *
 * Usage:
 *   threadView.configure(procurementOfferId, "procurer")
 *
 * The procurer may always send. The offerer may only reply once the procurer
 * has initiated the conversation (enforced server-side as well).
 */
export class OfferMessageThreadView extends UIView {
    
    readonly emptyStateLabel = new UITextView()
        .configuredWithObject({
            text: "Hindaja ei ole veel sõnumeid saatnud.",
            textColor: RHColors.textTimestamp,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                textAlign: "center",
                padding: `${RHSpacing.medium}px`
            }
        })
    
    readonly messageListView = new OfferMessageListView()
        .addedAsSubviewToView(this)
    
    readonly dividerView = new UIView()
        .configuredWithObject({
            style: { borderTop: `1px solid ${RHColors.dividerAlpha70.stringValue}` }
        })
        .addedAsSubviewToView(this)
    
    readonly inputArea = new RHTextArea(OFFER_MESSAGE_INPUT_ID)
        .configuredWithObject({
            placeholderText: "Kirjuta sõnum…"
        })
        .addedAsSubviewToView(this)
    
    readonly sendButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Saada" },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
        .addedAsSubviewToView(this)
    
    _procurementOfferId?: string
    _partId?: string
    _currentUserRole: "procurer" | "offerer" = "procurer"
    _messages: RHOfferMessage[] = []
    _isSending = NO
    
    constructor(elementID?: string) {
        super(elementID);
        (this as OfferMessageThreadView).configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: {
                border: `1px solid ${RHColors.borderSlate.stringValue}`,
                borderRadius: `${RHRadius.large}px`,
                overflow: "hidden"
            }
        })
        
        UIView.injectCSS(`
            #${OFFER_MESSAGE_INPUT_ID}::placeholder,
            #${OFFER_MESSAGE_INPUT_ID}_textElement::placeholder {
                color: ${RHColors.textPlaceholder.stringValue} !important;
                -webkit-text-fill-color: ${RHColors.textPlaceholder.stringValue} !important;
                opacity: 1;
            }
        `, "rh-offer-message-thread-input-placeholder")
        
        // noinspection JSConstantReassignment
        this.sendButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            void this._sendMessage()
        }
        
        // Ctrl/Cmd + Enter sends
        // noinspection JSConstantReassignment
        this.inputArea.controlEventTargetAccumulator.EnterDown = (_sender: UIView, event: Event) => {
            if (event instanceof KeyboardEvent && (event.ctrlKey || event.metaKey)) {
                void this._sendMessage()
            }
        }
    }
    
    // ── View tree lifecycle ───────────────────────────────────────────────────
    
    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        this.subscribeToThread()
    }
    
    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHCore.sharedInstance.offerStore.unsubscribeFromThread(this)
    }
    
    override didReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.didReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.newBuildAvailable) {
            // The server restarted and the socket reconnected. The previous
            // OfferMessageReceived socket listener is no longer active on
            // the new socket, so any messages sent while we were open would
            // never have triggered a reload. Fetch now to catch up.
            void this._loadMessages()
        }
    }
    
    // ── Public API ────────────────────────────────────────────────────────────
    
    async configure(procurementOfferId: string, currentUserRole: "procurer" | "offerer", partId: string) {
        this._procurementOfferId = procurementOfferId
        this._partId = partId
        this._currentUserRole = currentUserRole
        
        // Always register the subscription unconditionally. subscribeToThread
        // is idempotent — calling it again simply replaces the previous entry
        // for this subscriber. The previous guard (isMemberOfViewTree) caused
        // silent failures in UIDialogView contexts where tree-membership is not
        // yet true when configure() is called, even after showInRootView().
        this.subscribeToThread()
        
        await this._loadMessages()
        this._updateInputVisibility()
    }
    
    private subscribeToThread() {
        if (!this._procurementOfferId) {
            return
        }
        RHCore.sharedInstance.offerStore.subscribeToThread(
            this,
            this._procurementOfferId,
            () => {
                this._reloadIfNotSending()
            }
        )
    }

// ── Private helpers ───────────────────────────────────────────────────────
    
    /**
     * Reloads messages from the server unless a send is already in flight.
     * Called when the server pushes `OfferMessageReceived` for our thread.
     * We do a full reload rather than a single-message append because the
     * server marks messages as read during RetrieveOfferMessages — keeping
     * the read state consistent without extra round-trips.
     */
    private async _reloadIfNotSending() {
        if (this._isSending) {
            return
        }
        await this._loadMessages()
    }
    
    private async _loadMessages() {
        if (!this._procurementOfferId || !this._partId) {
            return
        }
        
        try {
            const result = (await SocketClient.RetrieveOfferMessages({
                procurementOfferId: this._procurementOfferId!,
                partId: this._partId!
            })).result
            
            this._messages = result.messages ?? []
            this.messageListView.setMessages(this._messages, this._currentUserRole)
            this._updateInputVisibility()
            this.setNeedsLayoutUpToRootView()
            
            // Mark messages from the other party as read, then reload the
            // unread-count map so this reader's own row/tab/accordion dots clear
            // immediately (the server push only refreshes the reader's NAV counts).
            SocketClient.MarkOfferMessagesAsRead({
                procurementOfferId: this._procurementOfferId!,
                partId: this._partId!
            }).then(() => {
                RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
            }).catch(error => {
                console.warn("[OfferMessageThreadView] MarkOfferMessagesAsRead failed:", error)
            })
            
            // Scroll to the bottom after layout settles
            setTimeout(() => {
                this.messageListView.scrollToBottom()
            }, 50)
        }
        catch (error) {
            console.error("[OfferMessageThreadView] Failed to load messages:", error)
        }
    }
    
    private _updateInputVisibility() {
        const hasMessages = this._messages.length > 0
        const canSend = this._currentUserRole === "procurer" || hasMessages
        
        if (canSend) {
            this.emptyStateLabel.removeFromSuperview()
        }
        else {
            // Offerer with no messages yet — show placeholder, hide input
            if (this.emptyStateLabel.superview !== this) {
                this.emptyStateLabel.addedAsSubviewToView(this)
            }
        }
        
        this.inputArea.enabled = canSend && !this._isSending
        this.sendButton.enabled = canSend && !this._isSending
    }
    
    private async _sendMessage() {
        if (!this._procurementOfferId || !this._partId) {
            return
        }
        
        const text = this.inputArea.text.trim()
        if (!text) {
            return
        }
        
        if (this._isSending) {
            return
        }
        
        this._isSending = YES
        this._updateInputVisibility()
        
        try {
            const result = (await SocketClient.SendOfferMessage({
                procurementOfferId: this._procurementOfferId!,
                partId: this._partId!,
                text
            })).result
            
            this.inputArea.text = ""
            this._messages.push(result.message)
            this.messageListView.appendMessage(result.message, this._currentUserRole)
            this._updateInputVisibility()
            this.setNeedsLayoutUpToRootView()
            
            setTimeout(() => {
                this.messageListView.scrollToBottom()
            }, 50)
        }
        catch (error) {
            console.error("[OfferMessageThreadView] Failed to send message:", error)
        }
        
        this._isSending = NO
        this._updateInputVisibility()
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.contentBoundsWithInset(0)
        const inputHeight = 44
        const footerHeight = inputHeight
            + RHSpacing.small
            + RHSpacing.small * 2
        const listHeight = [
            0,
            bounds.height - footerHeight - 1
        ].max()
        
        // Message list — fills the top portion (fixed height from parent)
        this.messageListView.frame = bounds.rectangleWithHeight(listHeight)
        
        // Empty state centred in the list area
        if (this.emptyStateLabel.superview === this) {
            this.emptyStateLabel.frame = this.messageListView.frame
        }
        
        // Divider
        this.dividerView.frame = this.messageListView.frame
            .rectangleForNextRow(0, 1)
        
        // Input row: textarea + send button
        this.dividerView.frame
            .rectangleForNextRow(RHSpacing.small, inputHeight)
            .rectangleWithInsets(
                RHSpacing.small,
                RHSpacing.small,
                0,
                0
            )
            .distributeViewsAlongWidth(
                [this.inputArea, this.sendButton],
                undefined,
                RHSpacing.small,
                [nil, 96]
            )
        
        this.sendButton.frame = this.sendButton.frame.rectangleWithHeight(
            (RHButtons.compactHeight + 2),
            0.5
        )
    }
    
    override intrinsicContentHeight(constrainingWidth?: number) {
        
        const result = 280
        
        return result
        
    }
    
    
}
